import React, { ChangeEvent } from "react";

/**
 * The text field (Shared chrome board, turn 8): well surface, neutral hairline,
 * muted2 placeholder; focus swaps the hairline for a 2px accent ring. An error is
 * a line, not a fill — flame label, flame hairline, flame helper under the field.
 *
 * The global stylesheet no longer forces input text white, so the field's own
 * `text-text` is the only colour rule it needs in either mode.
 */
type Props = {
  label?: string | React.JSX.Element | React.ReactNode;
  placeholder?: string;
  /** Legacy emphasis flag — kept as the accent label treatment. */
  highlighted?: boolean;
  /** Helper line under the field, drawn in flame; its presence is the error state. */
  error?: React.ReactNode;
  value?: string;
  setValue?: (value: string) => void;
  name?: string;
  defaultValue?: string;
  type?: "password" | "text" | "textarea" | "email";
  ref?: any;
  id?: string;
  maxLength?: number;
  onChange?: (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,
  ) => void;
  autoComplete?: string;
  pattern?: string;
  disabled?: boolean;
};

const FIELD =
  "w-full rounded-control bg-well text-text placeholder:text-muted2 p-4 outline-none " +
  "disabled:opacity-disabled ";

const hairline = (error: boolean) =>
  (error
    ? "shadow-[inset_0_0_0_1px_var(--s4s-flame)] "
    : "shadow-[inset_0_0_0_1px_var(--s4s-tint-neutral)] ") +
  "focus:shadow-[inset_0_0_0_2px_color-mix(in_srgb,var(--s4s-accent)_45%,transparent)] ";

function TextInput({
  label,
  name,
  highlighted = false,
  error,
  placeholder,
  defaultValue,
  value,
  setValue,
  type = "text",
  ref,
  id,
  onChange,
  maxLength,
  pattern,
  disabled,
  autoComplete,
}: Props) {
  const labelRow = label && (
    <div
      className={
        "text-sm mb-1 " +
        (error ? "text-flame" : highlighted ? "text-accent font-bold" : "text-muted")
      }
    >
      {label}
    </div>
  );
  const helper = error && <div className="text-explain text-flame mt-1">{error}</div>;

  if (type === "textarea")
    return (
      <div>
        {labelRow}
        <textarea
          name={name}
          defaultValue={defaultValue}
          placeholder={placeholder}
          value={value}
          maxLength={maxLength}
          onChange={(e) =>
            (onChange && onChange(e)) || (setValue && setValue(e.target.value))
          }
          disabled={disabled}
          className={FIELD + hairline(!!error) + "h-40"}
        />
        {helper}
      </div>
    );
  return (
    <div>
      {labelRow}
      <input
        id={id}
        ref={ref}
        type={type}
        name={name}
        defaultValue={defaultValue}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(e) =>
          (onChange && onChange(e)) || (setValue && setValue(e.target.value))
        }
        className={FIELD + hairline(!!error) + "min-h-[44px]"}
        disabled={disabled}
        autoComplete={autoComplete}
        pattern={pattern}
      />
      {helper}
    </div>
  );
}

export default TextInput;
